import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const CircularBackgroundTransition = ({
  children,
  bgColor = "#000000",
  textColor = "#ffffff",
}) => {
  const sectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  // Fix: use rootMargin with large values to prevent frequent re-triggering while scrolling
  const isInView = useInView(sectionRef, {
    amount: isMobile ? 0.3 : 0.6,
    margin: isMobile ? "0px 0px -30% 0px" : "-150px 0px -150px 0px",
  });

  useEffect(() => {
    const handleResize = () => {
      if (sectionRef.current) {
        sectionRef.current.style.height = "auto";
        const newHeight = sectionRef.current.offsetHeight;
        sectionRef.current.style.height = `${newHeight}px`;
        setIsMobile(window.innerWidth <= 768);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Recursively extract the first h2 element from the children and remove it from the content tree.
  const extractTitleAndContent = (children) => {
    let titleFound = null;

    const recursiveFilter = (child) => {
      if (React.isValidElement(child)) {
        // If we haven't found an H2 yet and this element is an H2, extract it.
        if (!titleFound && child.type === "h2") {
          titleFound = child;
          return null; // remove the title from the content
        }
        // If the element has children, recursively process them.
        if (child.props && child.props.children) {
          const newChildren = React.Children.map(
            child.props.children,
            recursiveFilter
          );
          return React.cloneElement(child, {
            ...child.props,
            children: newChildren,
          });
        }
      }
      return child;
    };

    const content = React.Children.map(children, recursiveFilter);
    return { title: titleFound, content };
  };

  const { title, content } = extractTitleAndContent(children);

  return (
    <motion.section
      ref={sectionRef}
      className="relative overflow-hidden bg-white text-black py-16 px-4 text-center flex flex-col justify-center"
    >
      <motion.div
        className="absolute top-1/2 left-1/2"
        style={{
          width: isMobile ? "200vh" : "max(200vw, 1000px)",
          height: isMobile ? "200vh" : "max(200vw, 1000px)",
          backgroundColor: bgColor,
          borderRadius: "50%",
          x: "-50%",
          y: "-50%",
          willChange: "transform",
        }}
        initial={{ scale: 0 }}
        animate={{ scale: isInView ? 1 : 0 }}
        transition={{
          scale: {
            duration: isInView ? 1 : 0.3,
            ease: isInView ? "easeInOut" : "easeOut",
          },
        }}
      />

      {/* Title rendered separately to avoid mix-blend-mode */}
      {title && (
        <motion.div
          className="relative z-[2]"
          animate={{
            color: isInView ? textColor : "#000000",
          }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
        >
          {title}
        </motion.div>
      )}

      {/* Remaining content with mix-blend-mode */}
      <div className="relative z-[2] mix-blend-difference text-white">
        {content}
      </div>
    </motion.section>
  );
};

export default CircularBackgroundTransition;
